import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TouchableOpacity, Platform, ScrollView, TextInput, StyleSheet } from 'react-native';
import { Picker } from '@react-native-picker/picker';

import { useAlertModal } from '../../common/hooks';
import { msgStr } from '../../common/constants/Message';
import BasicModalContainer from '../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../common/components/basicmodal/ModalFooter';
import { commonModalStyle } from '../../common/components/basicmodal';
import { getPenaltyRules } from '../../api/Penality';

interface Props {
  isModalVisible: boolean;
  closeModal: () => void;
  onSave: (penaltyData: any) => void;
  item?: any;
  index?: number;
}

const PenaltyModal = ({ isModalVisible, closeModal, onSave, item, index }: Props) => {

  if (Platform.OS !== 'web') return null;

  const { showAlert } = useAlertModal();

  const [penaltyData, setPenaltyData] = useState({
    returnedBattery: '',
    penaltyType: '',
    calculationType: '',
    unit: ''
  });

  const [calculationResult, setCalculationResult] = useState({
    amount: 0,
    description: '',
    penaltyResult: 0
  });

  const [penaltyRuleOptions, setPenaltyRuleOptions] = useState([]);
  const [allPenaltyRules, setAllPenaltyRules] = useState([]);

  const [selectedPenaltyRule, setSelectedPenaltyRule] = useState(null);

  // Function to get unique values from penalty rules
  const getUniquePenaltyTypes = (rules: any[]) => {
    const types = [...new Set(rules.map(rule => rule.type).filter(Boolean))];
    return [
      { label: 'Select', value: '' },
      ...types.map((type: string) => ({
        label: type.charAt(0).toUpperCase() + type.slice(1),
        value: type
      }))
    ];
  };

  const getUniqueUnits = (rules: any[]) => {
    const units = [...new Set(rules.map(rule => rule.applies_to).filter(Boolean))];
    return [
      { label: 'All', value: 'all' },
      ...units.map((unit: string) => ({
        label: unit.charAt(0).toUpperCase() + unit.slice(1),
        value: unit
      }))
    ];
  };

  const getUniqueCalculationTypes = (rules: any[]) => {
    const types = [...new Set(rules.map(rule => rule.calculation_type).filter(Boolean))];
    return [
      { label: 'Select', value: '' },
      ...types.map((type: string) => ({
        label: type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
        value: type
      }))
    ];
  };

  // Generate dynamic options from penalty rules
  const penaltyTypeOptions = getUniquePenaltyTypes(allPenaltyRules);
  const calculationTypeOptions = getUniqueCalculationTypes(
    penaltyData.penaltyType 
      ? allPenaltyRules.filter(rule => rule.type === penaltyData.penaltyType)
      : []
  );
  const unitOptions = getUniqueUnits(
    penaltyData.penaltyType && penaltyData.calculationType
      ? allPenaltyRules.filter(rule => 
          rule.type === penaltyData.penaltyType && 
          rule.calculation_type === penaltyData.calculationType
        )
      : []
  );

  const calculatePenalty = () => {
    if (!selectedPenaltyRule) {
      setCalculationResult({
        amount: 0,
        description: '',
        penaltyResult: 0
      });
      return;
    }

    let penaltyResult = 0;
    let description = selectedPenaltyRule.description || '';
    const basePrice = item?.price || 0; // Base price from item
    const expectedBattery = item?.battery_percent || 100; // Expected return battery percentage from item

    switch (selectedPenaltyRule.type) {
      case 'battery':
        const actualBattery = parseFloat(penaltyData.returnedBattery);
        if (!isNaN(actualBattery) && actualBattery >= 0 && actualBattery <= 100) {
          const difference = expectedBattery - actualBattery;
          
          if (difference > 0) {
            if (selectedPenaltyRule.calculation_type === 'per_unit') {
              penaltyResult = difference * parseFloat(selectedPenaltyRule.amount);
            } else if (selectedPenaltyRule.calculation_type === 'fixed') {
              penaltyResult = parseFloat(selectedPenaltyRule.amount);
            } else if (selectedPenaltyRule.calculation_type === 'percentage') {
              penaltyResult = basePrice * (parseFloat(selectedPenaltyRule.amount) / 100);
            }
            description = `Battery returned at ${actualBattery}% (${difference}% below required)`;
          }
        }
        break;

      case 'mileage':
        // This would need actual miles driven data
        if (selectedPenaltyRule.calculation_type === 'per_unit') {
          // Placeholder calculation - would need actual mileage data
          penaltyResult = parseFloat(selectedPenaltyRule.amount);
        }
        break;

      case 'delay':
        if (selectedPenaltyRule.calculation_type === 'fixed') {
          penaltyResult = parseFloat(selectedPenaltyRule.amount);
        } else if (selectedPenaltyRule.calculation_type === 'percentage') {
          penaltyResult = basePrice * (parseFloat(selectedPenaltyRule.amount) / 100);
        }
        break;

      case 'damage':
      case 'violation':
      case 'delivery':
        if (selectedPenaltyRule.calculation_type === 'fixed') {
          penaltyResult = parseFloat(selectedPenaltyRule.amount);
        } else if (selectedPenaltyRule.calculation_type === 'percentage') {
          penaltyResult = basePrice * (parseFloat(selectedPenaltyRule.amount) / 100);
        }
        break;

      default:
        penaltyResult = 0;
        break;
    }

    setCalculationResult({
      amount: parseFloat(selectedPenaltyRule.amount),
      description: description,
      penaltyResult: penaltyResult
    });
  };


  // Fetch all penalty rules on component mount
  useEffect(() => {
    getPenaltyRules({ active: true }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          if (jsonRes && jsonRes.data) {
            setAllPenaltyRules(jsonRes.data);
          }
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  }, []);

  // Filter penalty rules based on selected criteria
  useEffect(() => {
    if (penaltyData.penaltyType && penaltyData.calculationType && penaltyData.unit) {
      const filteredRules = allPenaltyRules.filter(rule => {
        const matchesType = rule.type === penaltyData.penaltyType;
        const matchesCalculation = rule.calculation_type === penaltyData.calculationType;
        const matchesUnit = rule.applies_to === penaltyData.unit || penaltyData.unit === 'all';
        return matchesType && matchesCalculation && matchesUnit;
      });
      setPenaltyRuleOptions(filteredRules);
    } else {
      setPenaltyRuleOptions([]);
    }
  }, [penaltyData.penaltyType, penaltyData.calculationType, penaltyData.unit, allPenaltyRules]);

  useEffect(() => {
    calculatePenalty();
  }, [selectedPenaltyRule, penaltyData.returnedBattery]);

  const resetForm = () => {
    setPenaltyData({
      returnedBattery: '',
      penaltyType: '',
      calculationType: '',
      unit: ''
    });
    setCalculationResult({
      amount: 0,
      description: '',
      penaltyResult: 0
    });
    setPenaltyRuleOptions([]);
    setSelectedPenaltyRule(null);
  };

  const handleCloseModal = () => {
    resetForm();
    closeModal();
  };

  const handleSave = () => {
    if (!penaltyData.returnedBattery || parseFloat(penaltyData.returnedBattery) < 0 || parseFloat(penaltyData.returnedBattery) > 100) {
      showAlert('error', 'Please enter valid battery percentage (0-100)');
      return;
    }

    if (!penaltyData.penaltyType) {
      showAlert('error', 'Please select a penalty type');
      return;
    }

    if (!penaltyData.calculationType) {
      showAlert('error', 'Please select a calculation type');
      return;
    }

    if (!penaltyData.unit) {
      showAlert('error', 'Please select a unit');
      return;
    }

    if (!selectedPenaltyRule) {
      showAlert('error', 'Please select a penalty rule');
      return;
    }

    const payload = {
      item,
      index,
      penalty: {
        returnedBattery: parseFloat(penaltyData.returnedBattery),
        penaltyType: penaltyData.penaltyType,
        calculationType: penaltyData.calculationType,
        unit: penaltyData.unit,
        calculatedPenalty: calculationResult.penaltyResult,
        selectedRule: {
          id: selectedPenaltyRule.id,
          rule_name: selectedPenaltyRule.rule_name,
          description: selectedPenaltyRule.description,
          amount: selectedPenaltyRule.amount,
          calculation_type: selectedPenaltyRule.calculation_type,
          amount_type: selectedPenaltyRule.amount_type
        }
      }
    };

    onSave(payload);
    handleCloseModal();
  };

  const handlePenaltyTypeChange = (value: string) => {
    setPenaltyData(prev => ({ 
      ...prev, 
      penaltyType: value,
      calculationType: '',
      unit: ''
    }));
    setSelectedPenaltyRule(null);
  };

  const handleCalculationTypeChange = (value: string) => {
    setPenaltyData(prev => ({ 
      ...prev, 
      calculationType: value,
      unit: ''
    }));
    setSelectedPenaltyRule(null);
  };

  const handleUnitChange = (value: string) => {
    setPenaltyData(prev => ({ ...prev, unit: value }));
    setSelectedPenaltyRule(null);
  };


  if (!isModalVisible) return null;

  return (
    <Modal animationType="none" transparent={true} visible={isModalVisible} onRequestClose={closeModal}>
      <BasicModalContainer contentStyle={{ width: '90%', maxWidth: 1020 }}>
        <ModalHeader label={'Penalty Calculator'} closeModal={closeModal} />
        <ModalBody>
          <ScrollView style={{ maxHeight: 500 }}>
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <View style={{ flex: 2, marginRight: 20 }}>
                <View style={styles.row}>
                  <View style={styles.column}>
                    <Text style={styles.label}>Penalty Type</Text>
                    <Picker
                      selectedValue={penaltyData.penaltyType}
                      onValueChange={handlePenaltyTypeChange}
                      style={styles.picker}
                    >
                      {penaltyTypeOptions.map((option) => (
                        <Picker.Item key={option.value} label={option.label} value={option.value} />
                      ))}
                    </Picker>
                  </View>

                  <View style={styles.column}>
                    <Text style={styles.label}>Calculation Type</Text>
                    <Picker
                      selectedValue={penaltyData.calculationType}
                      onValueChange={handleCalculationTypeChange}
                      style={styles.picker}
                    >
                      {calculationTypeOptions.map((option) => (
                        <Picker.Item key={option.value} label={option.label} value={option.value} />
                      ))}
                    </Picker>
                  </View>

                  <View style={styles.column}>
                    <Text style={styles.label}>Unit</Text>
                    <Picker
                      selectedValue={penaltyData.unit}
                      onValueChange={handleUnitChange}
                      style={styles.picker}
                    >
                      {unitOptions.map((option) => (
                        <Picker.Item key={option.value} label={option.label} value={option.value} />
                      ))}
                    </Picker>
                  </View>
                </View>

                <View style={styles.row}>

                  <View style={styles.column}>
                    <Text style={styles.label}>Expected Return (%)</Text>
                    <TextInput
                      style={styles.input}
                      value={item?.battery_percent?.toString() || '100'}
                      editable={false}
                      placeholder="Expected battery percentage"
                      keyboardType="numeric"
                    />
                  </View>
                
                  <View style={styles.column}>
                    <Text style={styles.label}>Actual Return (%)</Text>
                    <TextInput
                      style={styles.input}
                      value={penaltyData.returnedBattery}
                      onChangeText={(value) => setPenaltyData(prev => ({ ...prev, returnedBattery: value }))}
                      placeholder="Enter battery percentage"
                      keyboardType="numeric"
                    />
                  </View>
                </View>

                <View style={styles.row}>
                  <View style={styles.column}>
                    <Text style={styles.label}>Penalty Rule</Text>
                    <Picker
                      selectedValue={selectedPenaltyRule?.id || ''}
                      onValueChange={(value) => {
                        const rule = penaltyRuleOptions.find(r => parseInt(r.id) === parseInt(value));
                        setSelectedPenaltyRule(rule);
                      }}
                      style={styles.picker}
                    >
                      <Picker.Item key="" label="Select a rule" value="" />
                      {penaltyRuleOptions.map((rule) => (
                        <Picker.Item 
                          key={rule.id} 
                          label={rule.rule_name} 
                          value={rule.id} 
                        />
                      ))}
                    </Picker>
                  </View>
                </View>

              </View>

              <View style={{ flex: 1, minWidth: 250, maxWidth: 300 }}>
                <Text style={{ marginBottom: 8, fontSize: 20, fontWeight: 'bold' }}>Product Details</Text>
                {item && (
                  <View style={{ backgroundColor: '#f5f5f5', padding: 20, borderRadius: 8 }}>
                    <View>
                      <Text style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 5 }}>{item.display_name}</Text>
                      <Text style={{ fontSize: 14, color: '#666' }}>
                        Price: ${(item.price || 0).toLocaleString()}
                      </Text>
                    </View>
                  </View>
                )}

                {/* Penalty Result Section */}
                <View style={{ backgroundColor: '#f4f4f4', padding: 15, marginTop: 20, borderRadius: 8 }}>
                  <Text style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 10 }}>Penalty Details</Text>
                  <View style={{ marginBottom: 5 }}>
                    <Text style={{ fontSize: 14, color: '#666' }}>
                      <Text style={{ fontWeight: 'bold' }}>Amount:</Text> ${calculationResult.amount || '0'}
                    </Text>
                  </View>
                  <View style={{ marginBottom: 5 }}>
                    <Text style={{ fontSize: 14, color: '#666' }}>
                      <Text style={{ fontWeight: 'bold' }}>Description:</Text> {calculationResult.description || '-'}
                    </Text>
                  </View>
                  <View>
                    <Text style={{ fontSize: 14, color: '#666' }}>
                      <Text style={{ fontWeight: 'bold' }}>Penalty Result:</Text> ${calculationResult.penaltyResult || '0'}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </ScrollView>
        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={handleSave}>
            <Text style={styles.addButton}>{"Save"}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ...commonModalStyle,
  container: {
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 10,
  },
  column: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 5,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 12,
    fontSize: 14,
  },
  picker: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 12,
    height: 45,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 5,
    paddingHorizontal: 10,
  },
  checkboxLabel: {
    marginLeft: 10,
    fontSize: 14,
  },
});

export default PenaltyModal;
